import config from 'config';
import nprogress from 'nprogress';
import { webSocket } from 'rxjs/webSocket';
import { ajax } from 'rxjs/ajax';
import { map, catchError, takeUntil, timeout } from 'rxjs/operators';
import { of, Observable, Subject, timer } from 'rxjs';
import { dispatch, state } from '../store/index';
import { SET_APP_ERROR } from 'actionsConstants';
import { SET_INVALID_PROJECT, SET_LOADER_MSG } from 'app/appActions';
import { AJAX_ERROR, MULTI_ACTIONS_DISPATCH_DONE, WAIT_FOR_SOCKET } from 'actions';
import { showErrorAlert } from '__common/modules/alerts';
import { SET_PROJECT_ID_ON_AUTOSAVE } from 'projectDesign/components/projectConfiguration/projectConfigurationActions';

export function getToken(
  resource: string,
  body: {
    username?: string;
    password?: string;
    token?: string;
    withCredentials?: boolean;
  },
) {
  return {
    url: `${location.protocol}//${config.apiUrl}${resource}`,
    body,
    method: 'POST',
  };
}

export function hitApi(
  method: string,
  resource: string,
  body?: any,
  additioanlRequestsOpts?: object,
  oldUb?: boolean,
) {
  const token = state().user.token;
  const requestHeaders: {
    Accept: string;
    'Content-Type': string;
    Authorization?: string;
  } = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (token) {
    requestHeaders.Authorization = `Token ${token}`;
  }

  const apiUrl = oldUb ? config.apiUrlUb1 : config.apiUrl;

  const request = {
    url: `${location.protocol}//${apiUrl}${resource}`,
    headers: requestHeaders,
    body,
    method,
    ...additioanlRequestsOpts,
  };
  return request;
}

export function hitWebsockets(resource: string) {
  const wsProtocol = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${wsProtocol}://${config.apiUrl}${resource}`;
}

export function ObservableAjax(cfg: {
  link: apiData;
  socketName?: string;
  onSocketStart?: string;
  onSocketPending?: string;
  onSocketFailure?: string;
  until$?: Observable<any>;
  onError?: Function;
  onSuccess: any;
  onSuccessCallback?: Function; // New parameter for additional success handling
  onErrorMessage?: string;
  onErrorAction?: Function;
  nonReduxExtraAction?: Function;
  showBackendErrorMsgOnFailure?: boolean;
}) {
  const {
    link,
    socketName,
    until$,
    onError,
    onSuccess,
    onErrorMessage,
    onErrorAction,
    nonReduxExtraAction,
    showBackendErrorMsgOnFailure,
  } = cfg;

  return ajax(link).pipe(
    map(data => {
      if (data.status === 204) {
        return { status: 'SUCCESS' };
      }
      return data.response;
    }),
    map(tasks => {
      if (tasks.status === 'SUCCESS') {
        if (tasks.hasOwnProperty('error_msg')) {
          dispatch(SET_INVALID_PROJECT(true));
        }
      }
      if (tasks.status === 'STARTED' || tasks.status === 'PENDING') {
        nprogress.start();
        if (!socketName || socketName.length === 0) {
          console.log('Socket is going to open but socket name not provided');
          return;
        }

        setLoaderMsg(tasks, cfg);
        
        // Create WebSocket with proper configuration for RxJS 7+
        const wsUrl = hitWebsockets(`${socketName}/task/${tasks.task_id}/`);
        let taskSettled = false;
        const socket = webSocket({
          url: wsUrl,
          // Consumers compare the raw text "STATUS". The default serializer
          // would send a JSON string ("\"STATUS\"") and the status refresh
          // would be ignored, leaving the loader up after a missed broadcast.
          serializer: (value) => (typeof value === 'string' ? value : JSON.stringify(value)),
          openObserver: {
            next: () => {
              console.log('WebSocket connection opened for task:', tasks.task_id);
              // The completion broadcast is easy to miss: the task can finish
              // before this socket joins the channel group (common on the
              // second save, after an input such as wind speed changes). Ask
              // for the current Celery state once the socket is open.
              try {
                socket.next('STATUS');
              } catch (err) {
                console.log('Failed to request task status:', err);
              }
            }
          },
          closeObserver: {
            next: () => {
              console.log('WebSocket connection closed for task:', tasks.task_id);
            }
          }
        });
        
        // Create the main subscription with proper error handling and timeout
        const subscription = socket.pipe(
          until$ ? takeUntil(until$) : map(x => x), // Apply takeUntil only if until$ is provided
          timeout(300000) // 5 minute timeout to prevent hanging forever
        ).subscribe({
          next: (data: any | any[]) => {
            console.log('WebSocket received data:', data);

            if (taskSettled) {
              return;
            }
            
            if (data.status === 'SUCCESS') {
              taskSettled = true;
              console.log('Processing SUCCESS status');
              console.log('api responce with status SUCCESS');
              
              dispatch(SET_INVALID_PROJECT(false));
              if (data?.result?.pk) {
                dispatch(SET_PROJECT_ID_ON_AUTOSAVE(data?.result?.pk));
              }
              
              // Clear any loading indicators
              nprogress.done();
              dispatch(SET_LOADER_MSG(''));
              
              // Close the WebSocket connection
              unsubscribeSocket(socket);
              
              // Dispatch success actions
              if (Array.isArray(onSuccess)) {
                onSuccess.forEach((actionCreator, index) => {
                  console.log("actionCreator", actionCreator)
                  try {
                    if (!actionCreator) {
                      console.warn(`Action at index ${index} is undefined, skipping`);
                      return;
                    }

                    let actionResult: any;
                    try {
                      actionResult = actionCreator(data.result);
                    } catch (invokeErr) {
                      console.error(`Error executing onSuccess at index ${index}:`, invokeErr);
                      return;
                    }

                    if (typeof actionResult === 'function') {
                      // Thunk-like action
                      dispatch(actionResult as any);
                    } else if (actionResult && typeof actionResult === 'object' && typeof actionResult.type === 'string') {
                      dispatch(actionResult);
                    } else {
                      console.warn(`Invalid action result at index ${index}:`, actionResult);
                    }
                  } catch (err) {
                    console.log("actionCreator", actionCreator)
                    console.error(`Error dispatching success action at index ${index}:`, err);
                  }
                });
              } else if (onSuccess && typeof onSuccess === 'function') {
                try {
                  // If onSuccess is a function, call it with the result
                  const actionResult = onSuccess(data.result);

                  if (typeof actionResult === 'function') {
                    // Thunk-like action function
                    dispatch(actionResult as any);
                  } else if (actionResult && typeof actionResult === 'object' && typeof actionResult.type === 'string') {
                    // If the action was already dispatched in the onSuccess function,
                    // we don't need to dispatch it again
                    if (!(actionResult as any)._alreadyDispatched) {
                      dispatch(actionResult);
                    }
                  } else if (actionResult !== undefined) {
                    console.warn('Invalid action result:', actionResult);
                  }
                } catch (err) {
                  console.error('Error dispatching success action:', err);
                }
              }
              
              // Call the onSuccessCallback if provided
              if (cfg.onSuccessCallback && typeof cfg.onSuccessCallback === 'function') {
                try {
                  cfg.onSuccessCallback(data.result);
                } catch (err) {
                  console.error('Error in onSuccessCallback:', err);
                }
              }
            } else if (data.status === 'FAILURE' && onError) {
              taskSettled = true;
              console.log('Processing FAILURE status');
              nprogress.done();
              unsubscribeSocket(socket);
              dispatch(onError(data.result));
            } else if (data.status === 'PENDING') {
              console.log('Task is PENDING, waiting for completion...');
            } else if (data.status === 'STARTED') {
              console.log('Task has STARTED, processing...');
            } else if (data.status !== 'PENDING' && data.status !== 'STARTED') {
              taskSettled = true;
              console.log('Unknown status received:', data.status);
              
              // For unknown status, we'll try to proceed anyway to prevent UI hanging
              nprogress.done();
              unsubscribeSocket(socket);
              
              if (Array.isArray(onSuccess) && onSuccess.length > 0 && typeof onSuccess[0] === 'function') {
                try {
                  const actionResult = onSuccess[0](data.result || {});
                  if (actionResult && typeof actionResult === 'object' && actionResult.type) {
                    dispatch(actionResult);
                  } else {
                    console.warn('Invalid action result from unknown status handler:', actionResult);
                  }
                } catch (err) {
                  console.error('Error dispatching success action in unknown status handler:', err);
                }
              } else if (onSuccess && typeof onSuccess === 'function') {
                try {
                  const actionResult = onSuccess(data.result || {});
                  if (actionResult && typeof actionResult === 'object' && actionResult.type) {
                    dispatch(actionResult);
                  } else {
                    console.warn('Invalid action result from unknown status handler:', actionResult);
                  }
                } catch (err) {
                  console.error('Error dispatching success action in unknown status handler:', err);
                }
              }
              
              // Call the onSuccessCallback if provided
              if (cfg.onSuccessCallback && typeof cfg.onSuccessCallback === 'function') {
                try {
                  cfg.onSuccessCallback(data.result || {});
                } catch (err) {
                  console.error('Error in onSuccessCallback from unknown status handler:', err);
                }
              }
            }
            setLoaderMsg(data, cfg);
          },
          error: (error) => {
            if (taskSettled) {
              return;
            }
            taskSettled = true;
            console.log('WebSocket error:', error);
            nprogress.done();
            unsubscribeSocket(socket);
            
            // Handle timeout errors specifically
            if (error.name === 'TimeoutError') {
              console.log('WebSocket connection timed out');
              setLoaderMsg({ status: 'FAILURE' }, { onSocketFailure: 'Connection timed out. Proceeding with available data.' });
              
              // For timeouts, we'll try to proceed with whatever data we have
              // This helps prevent the UI from getting stuck in a loading state
              if (Array.isArray(onSuccess) && onSuccess.length > 0 && typeof onSuccess[0] === 'function') {
                try {
                  // If we have multiple success actions, dispatch the first one with empty data
                  // to ensure the UI updates and shows something
                  const emptyData = {
                    partsList: [],
                    accessoriesList: [],
                    attachmentsList: {},
                    tileReplacementList: {},
                    extraParameters: {},
                    total_watts: 0,
                    total_modules: 0,
                    project_name: '',
                    reload_selections: false,
                    created: false,
                    warningsInfo: { warnings: [] }
                  };
                  
                  const actionResult = onSuccess[0](emptyData);
                  if (actionResult && typeof actionResult === 'object' && actionResult.type) {
                    dispatch(actionResult);
                  } else {
                    console.warn('Invalid action result from timeout handler:', actionResult);
                  }
                } catch (err) {
                  console.error('Error dispatching success action in timeout handler:', err);
                }
              } else if (onSuccess && typeof onSuccess === 'function') {
                try {
                  // If we have a single success action, dispatch it with empty data
                  const emptyData = {
                    partsList: [],
                    accessoriesList: [],
                    attachmentsList: {},
                    tileReplacementList: {},
                    extraParameters: {},
                    total_watts: 0,
                    total_modules: 0,
                    project_name: '',
                    reload_selections: false,
                    created: false,
                    warningsInfo: { warnings: [] }
                  };
                  
                  const actionResult = onSuccess(emptyData);
                  if (actionResult && typeof actionResult === 'object' && actionResult.type) {
                    dispatch(actionResult);
                  } else {
                    console.warn('Invalid action result from timeout handler:', actionResult);
                  }
                } catch (err) {
                  console.error('Error dispatching success action in timeout handler:', err);
                }
              }
              
              // Also dispatch the error action if provided
              if (onError) {
                dispatch(onError({ message: 'Connection timed out' }));
              }
            } else {
              if (onError) {
                dispatch(onError(error));
              }
              setLoaderMsg({ status: 'FAILURE' }, cfg);
            }
          },
          complete: () => {
            console.log('WebSocket connection completed');
            unsubscribeSocket(socket);
          }
        });


        return WAIT_FOR_SOCKET();
      }

      if (Array.isArray(onSuccess)) {
        if (nonReduxExtraAction) {
          nonReduxExtraAction(tasks.result);
        }
        onSuccess.map((action, index) => {
          if (index === 0) {
            dispatch(action(tasks.result || tasks));
            if (nonReduxExtraAction) {
              nonReduxExtraAction(tasks.result);
            }
            return;
          }
          dispatch(action(tasks.result || tasks));
          if (nonReduxExtraAction) {
            nonReduxExtraAction(tasks.result);
          }
        });
        return MULTI_ACTIONS_DISPATCH_DONE();
      }

      if (nonReduxExtraAction) {
        nonReduxExtraAction(tasks.result);
      }
      return onSuccess(tasks.result || tasks);
    }),
    catchError(ajaxError => {
      if (ajaxError.response !== null && typeof ajaxError.response === 'object' && ajaxError.response['invalid_project']) {
        const projectId = ajaxError.response['project_id'];
        dispatch(SET_PROJECT_ID_ON_AUTOSAVE(projectId));
        dispatch(SET_INVALID_PROJECT(true));
      }
      if (onErrorMessage) {
        let errorString = `Error: ${onErrorMessage}`;
        if (ajaxError?.response && ajaxError?.response['error_message']) {
          showErrorAlert(`Error: ${ajaxError.response['error_message']}`);
          errorString = `Error: ${ajaxError.response['error_message']}`;
        } else {
          showErrorAlert(onErrorMessage);
        }
        if (showBackendErrorMsgOnFailure) {
          const _ajaxError = ajaxError?.response?.result?.[0];
          if (_ajaxError) errorString += `, ${_ajaxError}`;
        }
        setLoaderMsg({ status: 'FAILURE' }, { onSocketFailure: errorString });
      }

      if (onErrorAction) {
        onErrorAction(ajaxError.response);
      }

      console.log('Message error', ajaxError.message);

      if (ajaxError.status !== 0 && ajaxError.request && !ajaxError.request.url.includes('api/v1/get_environmental_factors/') && ajaxError.status >= 500) {
        dispatch(SET_APP_ERROR(JSON.stringify(ajaxError, undefined, 4)));
      }

      if (onError) {
        return of(onError(ajaxError));
      }
      return of(AJAX_ERROR());
    })
  );
}

function unsubscribeSocket(socket) {
  try {
    // In RxJS 7+, WebSocket subjects have a complete() method to properly close the connection
    if (socket && typeof socket.complete === 'function') {
      socket.complete();
    }
    // Also call unsubscribe if it exists (for backward compatibility)
    if (socket && typeof socket.unsubscribe === 'function') {
      socket.unsubscribe();
    }
  } catch (error) {
    console.log('Socket already unsubscribed or error during unsubscription:', error);
  }
}

function cancelSocketOn(socket, notifier$: Subject<any>) {
  try {
    // Return the subscription so it can be managed properly
    return socket.pipe(takeUntil(notifier$)).subscribe({
      next: () => {}, // Empty next handler
      error: (error) => console.log('Socket error in cancelSocketOn:', error),
      complete: () => console.log('Socket completed in cancelSocketOn')
    });
  } catch (error) {
    console.log('Failed to apply takeUntil on socket:', error);
    return null;
  }
}


function setLoaderMsg(tasks, cfg) {
  const { onSocketStart, onSocketPending, onSocketFailure } = cfg;
  if (tasks.status === 'STARTED') {
    dispatch(SET_LOADER_MSG(onSocketStart));
  }

  if (tasks.status === 'PENDING') {
    dispatch(SET_LOADER_MSG(onSocketPending));
  }

  if (tasks.status === 'FAILURE') {
    dispatch(SET_LOADER_MSG(tasks.result || onSocketFailure));
  }
}
